import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import {
  getMOSProcurementList,
  reserveMOSItemStock,
  bulkReserveMOSItemStock,
  releaseMOSItemStock,
  updateMOSProcurementStatus,
  createIndentForMOSShortage,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ── Theme-driven status / priority meta (no hardcoded hex) ─────────────────
const STATUS_META = {
  NOT_ORDERED: { label: "Not Ordered", icon: WarningAmberIcon, key: "error" },
  ORDERED: { label: "Ordered", icon: InventoryIcon, key: "warning" },
  IN_TRANSIT: { label: "In Transit", icon: LocalShippingIcon, key: "info" },
  ARRIVED: { label: "Arrived", icon: CheckCircleOutlineIcon, key: "success" },
  RESERVED: { label: "Reserved", icon: LockIcon, key: "secondary" },
  ISSUED: { label: "Issued", icon: DoneAllIcon, key: "primary" },
};
const PRIORITY_META = { Low: "success", Normal: "info", High: "error" };
const ALLOWED_STATUSES = [
  "NOT_ORDERED",
  "ORDERED",
  "IN_TRANSIT",
  "ARRIVED",
  "RESERVED",
];

// ── Header cell style helper — always solid, theme aware ───────────────────
const headerCellSx = (theme) => ({
  fontWeight: 700,
  fontSize: "0.74rem",
  bgcolor:
    theme.palette.mode === "dark"
      ? theme.palette.primary.dark
      : theme.palette.primary.light,
  color:
    theme.palette.mode === "dark"
      ? theme.palette.primary.contrastText
      : theme.palette.getContrastText(theme.palette.primary.light),
});

// ── Status Chip ────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const theme = useTheme();
  const meta = STATUS_META[status] || STATUS_META.NOT_ORDERED;
  const color = theme.palette[meta.key]?.main || theme.palette.grey[600];
  const Icon = meta.icon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 13 }} />}
      label={meta.label}
      size="small"
      sx={{
        backgroundColor: alpha(
          color,
          theme.palette.mode === "dark" ? 0.2 : 0.12,
        ),
        color,
        fontWeight: 600,
        fontSize: "0.66rem",
        height: 22,
        border: `1px solid ${alpha(color, 0.3)}`,
        "& .MuiChip-icon": { color },
      }}
    />
  );
}

// ── Stock Bar ──────────────────────────────────────────────────────────────
function StockBar({ current, reserved, free }) {
  const theme = useTheme();
  const pct = current > 0 ? Math.min((reserved / current) * 100, 100) : 0;
  const color =
    pct > 80
      ? theme.palette.error.main
      : pct > 40
        ? theme.palette.warning.main
        : theme.palette.success.main;
  return (
    <Tooltip
      title={`Reserved: ${reserved} / Current: ${current} / Free: ${free}`}
    >
      <Box
        sx={{
          width: 90,
          height: 5,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.text.primary, 0.08),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            bgcolor: color,
            borderRadius: 3,
            transition: "width 0.4s ease",
          }}
        />
      </Box>
    </Tooltip>
  );
}

// ── Item Row (expanded / compact, zebra striped) ────────────────────────────
function ItemRow({
  line,
  index,
  mosId,
  soNo,
  reserveQty,
  onReserveQtyChange,
  onActionDone,
}) {
  const theme = useTheme();
  const [releaseQty, setReleaseQty] = useState("");
  const [saving, setSaving] = useState(false);
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState(line.procurement_status);

  const shortage = Number(line.shortage_qty || 0);
  const hasShortage = shortage > 0;

  const handleReserve = async () => {
    const qty = parseFloat(reserveQty);
    if (!qty || qty <= 0) {
      toast.warn("Enter a valid quantity to reserve.");
      return;
    }
    try {
      setSaving(true);
      await reserveMOSItemStock(line.line_id, qty);
      toast.success(`Reserved ${qty} ${line.unit} for MOS ${soNo}`);
      onReserveQtyChange(line.line_id, "");
      onActionDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to reserve stock.");
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async () => {
    const qty = parseFloat(releaseQty);
    if (!qty || qty <= 0) {
      toast.warn("Enter a valid quantity to release.");
      return;
    }
    try {
      setSaving(true);
      await releaseMOSItemStock(line.line_id, qty);
      toast.success(`Released ${qty} ${line.unit} reservation for MOS ${soNo}`);
      setReleaseQty("");
      onActionDone();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Failed to release reservation.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      setSaving(true);
      await updateMOSProcurementStatus(line.line_id, newStatus);
      toast.success("Procurement status updated.");
      setEditStatusOpen(false);
      onActionDone();
    } catch (err) {
      toast.error("Failed to update status.");
    } finally {
      setSaving(false);
    }
  };

  // ── Zebra stripe: one shade always white/paper, other a light tint ──────
  const stripeBg =
    index % 2 === 0
      ? theme.palette.background.paper
      : alpha(
          theme.palette.text.primary,
          theme.palette.mode === "dark" ? 0.045 : 0.035,
        );

  const rowBg = hasShortage
    ? alpha(
        theme.palette.error.main,
        theme.palette.mode === "dark" ? 0.12 : 0.07,
      )
    : stripeBg;

  return (
    <TableRow
      sx={{
        backgroundColor: rowBg,
        "&:hover": {
          backgroundColor: alpha(theme.palette.text.primary, 0.06),
        },
      }}
    >
      <TableCell sx={{ fontSize: "0.74rem", minWidth: 160, py: 0.75 }}>
        <Typography sx={{ fontSize: "0.74rem", lineHeight: 1.3 }}>
          {line.item_name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem" }}
        >
          {line.category}
        </Typography>
      </TableCell>

      <TableCell
        align="center"
        sx={{ fontSize: "0.78rem", fontWeight: 600, py: 0.75 }}
      >
        {line.required_qty}
      </TableCell>
      <TableCell align="center" sx={{ py: 0.75 }}>
        <Chip
          label={line.reserved_for_mos}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.68rem",
            bgcolor: alpha(theme.palette.info.main, 0.12),
            color: theme.palette.info.main,
            fontWeight: 700,
          }}
        />
      </TableCell>
      <TableCell align="center" sx={{ py: 0.75 }}>
        <Chip
          label={line.issued_qty}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.68rem",
            bgcolor: alpha(theme.palette.success.main, 0.12),
            color: theme.palette.success.main,
            fontWeight: 700,
          }}
        />
      </TableCell>
      <TableCell align="center" sx={{ py: 0.75 }}>
        {hasShortage ? (
          <Chip
            label={`-${shortage}`}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.68rem",
              bgcolor: alpha(theme.palette.error.main, 0.12),
              color: theme.palette.error.main,
              fontWeight: 700,
            }}
          />
        ) : (
          <Chip
            label="✓"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.68rem",
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: theme.palette.success.main,
              fontWeight: 700,
            }}
          />
        )}
      </TableCell>

      <TableCell align="center" sx={{ py: 0.75 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.4,
          }}
        >
          <Typography sx={{ fontSize: "0.66rem", color: "text.secondary" }}>
            <b>{line.free_stock}</b> free / {line.current_stock}
          </Typography>
          <StockBar
            current={line.current_stock}
            reserved={line.total_reserve}
            free={line.free_stock}
          />
        </Box>
      </TableCell>

      <TableCell align="center" sx={{ py: 0.75 }}>
        <StatusChip status={line.procurement_status} />
      </TableCell>

      <TableCell sx={{ py: 0.75, minWidth: 200 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Qty"
            type="number"
            value={reserveQty || ""}
            onChange={(e) => onReserveQtyChange(line.line_id, e.target.value)}
            sx={{
              width: 62,
              "& input": { fontSize: "0.7rem", py: "3px", px: "6px" },
            }}
            inputProps={{ min: 0 }}
          />
          <Tooltip title="Reserve free stock for this item">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={handleReserve}
                disabled={
                  saving ||
                  !reserveQty ||
                  Number(reserveQty) <= 0 ||
                  line.free_stock <= 0
                }
                sx={{
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 1,
                  p: 0.5,
                }}
              >
                {saving ? (
                  <CircularProgress size={12} />
                ) : (
                  <LockIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          {line.reserved_for_mos > 0 && (
            <>
              <TextField
                size="small"
                placeholder="Qty"
                type="number"
                value={releaseQty}
                onChange={(e) => setReleaseQty(e.target.value)}
                sx={{
                  width: 62,
                  "& input": { fontSize: "0.7rem", py: "3px", px: "6px" },
                }}
                inputProps={{ min: 0 }}
              />
              <Tooltip title="Release this reservation back to free stock">
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={handleRelease}
                    disabled={saving || !releaseQty}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  >
                    <LockOpenIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}

          <Tooltip title="Update procurement status">
            <IconButton
              size="small"
              onClick={() => {
                setNewStatus(line.procurement_status);
                setEditStatusOpen(true);
              }}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 0.5,
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>

      <Dialog
        open={editStatusOpen}
        onClose={() => setEditStatusOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, fontSize: "1rem" }}>
          Update Procurement Status
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            {line.item_name}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {ALLOWED_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_META[s]?.label || s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditStatusOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdate}
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
}

// ── MOS Row (expandable, zebra striped) ─────────────────────────────────────
function MOSRow({ mos, index, open, onToggle, onActionDone }) {
  const theme = useTheme();
  const [reserveInputs, setReserveInputs] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [indentCreating, setIndentCreating] = useState(false);

  const totalItems = mos.items.length;
  const shortageItems = mos.items.filter(
    (i) => Number(i.shortage_qty) > 0,
  ).length;
  const reservedItems = mos.items.filter(
    (i) =>
      i.procurement_status === "RESERVED" || i.procurement_status === "ISSUED",
  ).length;

  const handleReserveQtyChange = (lineId, value) => {
    setReserveInputs((prev) => ({ ...prev, [lineId]: value }));
  };

  const handleCreateIndentForShortage = async (e) => {
    e?.stopPropagation();
    if (shortageItems === 0) {
      toast.info("No shortage items found for this MOS.");
      return;
    }
    try {
      setIndentCreating(true);
      const res = await createIndentForMOSShortage(mos.mos_id);
      toast.success(
        res.message || "Indent generated successfully for shortage items!",
      );
      onActionDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to generate Indent.");
    } finally {
      setIndentCreating(false);
    }
  };

  const handleAutoFillAvailable = (e) => {
    e?.stopPropagation();
    const newInputs = {};
    let count = 0;
    mos.items.forEach((line) => {
      const shortage = Number(line.shortage_qty || 0);
      const free = Number(line.free_stock || 0);
      if (shortage > 0 && free > 0) {
        newInputs[line.line_id] = Math.min(shortage, free);
        count++;
      }
    });
    setReserveInputs(newInputs);
    if (count > 0) {
      toast.info(
        `Auto-filled ${count} item(s) with available free stock. Click "Reserve All Filled" to confirm.`,
      );
    } else {
      toast.warn(
        "No available free stock found to reserve for shortage items.",
      );
    }
  };

  const handleClearInputs = (e) => {
    e?.stopPropagation();
    setReserveInputs({});
  };

  const filledItemsList = Object.entries(reserveInputs)
    .filter(([_, val]) => Number(val) > 0)
    .map(([lineId, val]) => ({ line_id: Number(lineId), qty: Number(val) }));

  const handleBulkReserve = async (e) => {
    e?.stopPropagation();
    if (filledItemsList.length === 0) {
      toast.warn("Please enter or auto-fill quantities first.");
      return;
    }
    try {
      setBulkSaving(true);
      const res = await bulkReserveMOSItemStock(filledItemsList);
      toast.success(
        res.message ||
          `Successfully reserved stock for ${res.reserved_count} item(s).`,
      );
      if (res.errors && res.errors.length > 0) {
        res.errors.forEach((errMsg) => toast.warn(errMsg));
      }
      setReserveInputs({});
      onActionDone();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Failed to bulk reserve stock.",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const priorityColorKey = PRIORITY_META[mos.priority] || "info";
  const priorityColor = theme.palette[priorityColorKey]?.main;

  // ── Zebra stripe for main table rows — one shade always white/paper ─────
  const rowStripeBg =
    index % 2 === 0
      ? theme.palette.background.paper
      : alpha(
          theme.palette.text.primary,
          theme.palette.mode === "dark" ? 0.045 : 0.035,
        );

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: "pointer",
          backgroundColor: rowStripeBg,
          "& > td": { borderBottom: open ? 0 : undefined },
          "&:hover": {
            backgroundColor: alpha(theme.palette.text.primary, 0.06),
          },
        }}
        onClick={onToggle}
      >
        <TableCell padding="checkbox" sx={{ py: 0.75, bgcolor: "inherit" }}>
          <IconButton size="small">
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell sx={{ py: 0.75, minWidth: 110, bgcolor: "inherit" }}>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "0.8rem", lineHeight: 1.3 }}
          >
            {mos.so_no}
          </Typography>
        </TableCell>
        <TableCell
          sx={{
            py: 0.75,
            fontSize: "0.78rem",
            minWidth: 140,
            bgcolor: "inherit",
          }}
        >
          {mos.project_name || "—"}
        </TableCell>
        <TableCell
          sx={{
            py: 0.75,
            fontSize: "0.78rem",
            minWidth: 120,
            bgcolor: "inherit",
          }}
        >
          {mos.party_name}
        </TableCell>
        <TableCell sx={{ py: 0.75, bgcolor: "inherit" }}>
          <Chip
            label={mos.priority}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.66rem",
              bgcolor: alpha(priorityColor, 0.14),
              color: priorityColor,
              fontWeight: 700,
            }}
          />
        </TableCell>
        <TableCell
          sx={{
            py: 0.75,
            fontSize: "0.76rem",
            whiteSpace: "nowrap",
            bgcolor: "inherit",
          }}
        >
          {mos.delivery_date || "—"}
        </TableCell>
        <TableCell sx={{ py: 0.75, bgcolor: "inherit" }}>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Chip
              label={`${totalItems} items`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.64rem",
                bgcolor: alpha(theme.palette.info.main, 0.12),
                color: theme.palette.info.main,
              }}
            />
            {shortageItems > 0 && (
              <Chip
                label={`${shortageItems} short`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.64rem",
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  color: theme.palette.error.main,
                }}
              />
            )}
            {reservedItems > 0 && (
              <Chip
                label={`${reservedItems} covered`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.64rem",
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.main,
                }}
              />
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, borderBottom: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 1,
                mb: 1.5,
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                overflow: "hidden",
                bgcolor: theme.palette.background.paper,
              }}
            >
              {/* Bulk Action Bar */}
              <Box
                sx={{
                  p: 1,
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: alpha(
                    theme.palette.primary.main,
                    theme.palette.mode === "dark" ? 0.12 : 0.06,
                  ),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="primary"
                    sx={{ fontSize: "0.78rem" }}
                  >
                    Line Items — {mos.so_no}
                  </Typography>
                  {filledItemsList.length > 0 && (
                    <Chip
                      label={`${filledItemsList.length} ready`}
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontWeight: 700, fontSize: "0.66rem" }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexWrap: "wrap",
                  }}
                >
                  <Tooltip title="Auto-fill available free stock for shortage items">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FlashOnIcon sx={{ fontSize: 14 }} />}
                      onClick={handleAutoFillAvailable}
                      sx={{
                        fontSize: "0.68rem",
                        textTransform: "none",
                        fontWeight: 600,
                        height: 26,
                        px: 1,
                      }}
                    >
                      Auto-Fill
                    </Button>
                  </Tooltip>

                  {filledItemsList.length > 0 && (
                    <Tooltip title="Clear filled inputs">
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        startIcon={<ClearAllIcon sx={{ fontSize: 14 }} />}
                        onClick={handleClearInputs}
                        sx={{
                          fontSize: "0.68rem",
                          textTransform: "none",
                          height: 26,
                          px: 1,
                        }}
                      >
                        Clear
                      </Button>
                    </Tooltip>
                  )}

                  <Tooltip title="Reserve all filled quantities">
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={
                          bulkSaving ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : (
                            <DoneAllIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        onClick={handleBulkReserve}
                        disabled={bulkSaving || filledItemsList.length === 0}
                        sx={{
                          fontSize: "0.68rem",
                          textTransform: "none",
                          fontWeight: 700,
                          height: 26,
                          px: 1.5,
                        }}
                      >
                        {bulkSaving
                          ? "Reserving…"
                          : `Reserve (${filledItemsList.length})`}
                      </Button>
                    </span>
                  </Tooltip>

                  {/* Indent Shortage Generation - Commented out for now */}
                  {/* {shortageItems > 0 && (
                    <Tooltip title="Generate an Indent automatically for all shortage items of this MOS">
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={
                            indentCreating ? (
                              <CircularProgress size={12} color="inherit" />
                            ) : (
                              <AddShoppingCartIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          onClick={handleCreateIndentForShortage}
                          disabled={indentCreating}
                          sx={{
                            fontSize: "0.68rem",
                            textTransform: "none",
                            fontWeight: 700,
                            height: 26,
                            px: 1.2,
                          }}
                        >
                          {indentCreating
                            ? "Creating Indent…"
                            : `Indent Shortages (${shortageItems})`}
                        </Button>
                      </span>
                    </Tooltip>
                  )} */}
                </Box>
              </Box>

              <TableContainer
                sx={{
                  overflow: "auto",
                  maxHeight: { xs: 260, sm: 320 },
                }}
              >
                <Table stickyHeader size="small" sx={{ minWidth: 780 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx(theme)}>Item</TableCell>
                      <TableCell align="center" sx={headerCellSx(theme)}>
                        Req.
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx(theme)}>
                        Reserved
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx(theme)}>
                        Issued
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx(theme)}>
                        Shortage
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx(theme)}>
                        Stock
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx(theme)}>
                        Status
                      </TableCell>
                      <TableCell sx={headerCellSx(theme)}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mos.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            No items in this MOS.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      mos.items.map((line, idx) => (
                        <ItemRow
                          key={line.line_id}
                          line={line}
                          index={idx}
                          mosId={mos.mos_id}
                          soNo={mos.so_no}
                          reserveQty={reserveInputs[line.line_id] || ""}
                          onReserveQtyChange={handleReserveQtyChange}
                          onActionDone={onActionDone}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────
export default function MOSProcurementDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedMosId, setExpandedMosId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const divisionId = localStorage.getItem("division_id");
      const resp = await getMOSProcurementList({
        division_id: divisionId || undefined,
      });
      setData(resp.results || []);
    } catch (err) {
      toast.error("Failed to load MOS procurement data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = data.filter(
    (mos) =>
      !search ||
      mos.so_no?.toLowerCase().includes(search.toLowerCase()) ||
      mos.party_name?.toLowerCase().includes(search.toLowerCase()) ||
      mos.project_name?.toLowerCase().includes(search.toLowerCase()),
  );

  const totalMOS = data.length;
  const totalShortage = data.reduce(
    (acc, m) => acc + m.items.filter((i) => Number(i.shortage_qty) > 0).length,
    0,
  );
  const totalReserved = data.reduce(
    (acc, m) =>
      acc + m.items.filter((i) => i.procurement_status === "RESERVED").length,
    0,
  );

  const summaryCards = [
    { label: "Active MOS Orders", value: totalMOS, key: "primary" },
    { label: "Items with Shortage", value: totalShortage, key: "error" },
    { label: "Fully Reserved Items", value: totalReserved, key: "success" },
  ];

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 2 },
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 70px)",
        minHeight: 0,
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Summary Cards — compact, title left / value right */}
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          mb: 1.5,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        {summaryCards.map((card) => {
          const color = theme.palette[card.key].main;
          return (
            <Paper
              key={card.label}
              elevation={0}
              sx={{
                px: 1.75,
                py: 1,
                borderRadius: 2,
                flex: "1 1 150px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderLeft: `3px solid ${color}`,
                bgcolor: alpha(
                  color,
                  theme.palette.mode === "dark" ? 0.1 : 0.05,
                ),
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: "0.72rem" }}
              >
                {card.label}
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color, fontSize: "1.15rem" }}
              >
                {card.value}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Search + Refresh — same line */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexShrink: 0 }}>
        <TextField
          placeholder="Search by MOS No., Party, or MOS…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiInputBase-root": { bgcolor: "background.paper" } }}
        />
        {isMobile ? (
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={fetchData}
                disabled={loading}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  flexShrink: 0,
                }}
              >
                {loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            onClick={fetchData}
            disabled={loading}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            Refresh
          </Button>
        )}
      </Box>

      {/* Table — only this area scrolls, header sticky */}
      {loading && data.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No active MOS orders found.</Alert>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={headerCellSx(theme)} />
                <TableCell sx={headerCellSx(theme)}>MOS No.</TableCell>
                <TableCell sx={headerCellSx(theme)}>MOS Name</TableCell>
                <TableCell sx={headerCellSx(theme)}>Party</TableCell>
                <TableCell sx={headerCellSx(theme)}>Priority</TableCell>
                <TableCell sx={headerCellSx(theme)}>Delivery</TableCell>
                <TableCell sx={headerCellSx(theme)}>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((mos, idx) => (
                <MOSRow
                  key={mos.mos_id}
                  mos={mos}
                  index={idx}
                  open={expandedMosId === mos.mos_id}
                  onToggle={() =>
                    setExpandedMosId((prev) =>
                      prev === mos.mos_id ? null : mos.mos_id,
                    )
                  }
                  onActionDone={fetchData}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
